import React, { useState } from 'react';
import './ApplicationDetail.css';

const ApplicationDetail = ({ application, onBack, onStatusUpdate, onDelete }) => {
  const [newStatus, setNewStatus] = useState(application.status || 'not submited');
  const [selectedCourse, setSelectedCourse] = useState(application.selectedCourse || '');

  const getStatusColor = (status) => {
    switch (status) {
      case 'selected':
        return '#28a745';
      case 'approoved':
        return '#fd7e14';
      case 'rejected':
        return '#dc3545';
      case 'submited':
        return '#0d6efd';
      default:
        return '#6c757d';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatApplicantName = () => {
    const firstName = application.applicantFirstName || '';
    const middleName = application.applicantMiddleName || '';
    const lastName = application.applicantLastName || '';
    
    const middleInitial = middleName ? middleName.charAt(0).toUpperCase() + '.' : '';
    const fullName = `${firstName} ${middleInitial} ${lastName}`.trim();
    
    return fullName || 'N/A';
  };

  const handleStatusUpdate = () => {
    onStatusUpdate(application.id, newStatus, selectedCourse);
  };

  const handleDelete = () => {
    onDelete(application.id);
  };

  return (
    <div className="application-detail">
      <div className="detail-header">
        <button onClick={onBack} className="back-btn">
          ← Back to List
        </button>
        <h2>Application Details</h2>
        <div className="header-actions">
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            className="status-select"
          >
            <option value="not submited">Not Submitted</option>
            <option value="submited">Submitted</option>
            <option value="approoved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="selected">Selected</option>
          </select>
          
          {newStatus === "selected" && (
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="course-select"
            >
              <option value="">Select Course</option>
              {application.firstChoice && <option value={application.firstChoice}>{application.firstChoice}</option>}
              {application.secondChoice && <option value={application.secondChoice}>{application.secondChoice}</option>}
              {application.thirdChoice && <option value={application.thirdChoice}>{application.thirdChoice}</option>}
            </select>
          )}
          
          <button onClick={handleStatusUpdate} className="update-btn">
            Update Status
          </button>
          <button onClick={handleDelete} className="delete-btn">
            Delete Application
          </button>
        </div>
      </div>

      <div className="detail-content">
        {/* Application Status */}
        <div className="detail-section">
          <h3>Application Status</h3>

          <div className="status-display">
            <span 
              className="status-badge"
              style={{ backgroundColor: getStatusColor(application.status) }}
            >
              {application.status || 'Not Submitted'}
            </span>
            <p>Submitted: {formatDate(application.submittedAt)}</p>
            {application.updatedAt && (
              <p>Last Updated: {formatDate(application.updatedAt)}</p>
            )}
            {application.updatedBy && (
              <p>Updated By: {application.updatedBy}</p>
            )}
            {application.status === "selected" && application.selectedCourse && (
              <p><strong>Selected Course:</strong> {application.selectedCourse}</p>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="detail-section">
          <h3>Personal Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>First Name:</label>
              <span>{application.applicantFirstName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Middle Name:</label>
              <span>{application.applicantMiddleName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Last Name:</label>
              <span>{application.applicantLastName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{application.applicantEmail || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Phone:</label>
              <span>{application.applicantPhoneNumber || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Date of Birth:</label>
              <span>{application.applicantDateOfBirth || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Gender:</label>
              <span>{application.applicantGender || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Nationality:</label>
              <span>{application.applicantNationality || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Address:</label>
              <span>{application.applicantAddress || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Passport Number:</label>
              <span>{application.applicantPassportNumber || 'N/A'}</span>
            </div>

          </div>
        </div>

        {/* Course Selection */}
        <div className="detail-section">
          <h3>Course Selection</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Level:</label>
              <span>{application.level || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>First Choice:</label>
              <span>{application.firstChoice || 'N/A'} ({application.firstChoiceDuration})</span>
            </div>
            <div className="info-item">
              <label>Second Choice:</label>
              <span>{application.secondChoice || 'N/A'} ({application.secondChoiceDuration})</span>
            </div>
            <div className="info-item">
              <label>Third Choice:</label>
              <span>{application.thirdChoice || 'N/A'} ({application.thirdChoiceDuration})</span>
            </div>
          </div>
        </div>
        {/* Academic Information */}
        <div className="detail-section">
          <h3>Academic Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>HIGH SCHOOL INDEX NO:</label>
              <span>{application.formSixIndexNumber || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label> HIGH SCHOOL Graduation Year:</label>
              <span>{application.yearCompletedFormSixOrAdvanced || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>DIPLOMA INSTITUTE NAME:</label>
              <span>{application.nameOfInstituteCompletedDiploma || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>DIPLOMA STARTED YEAR</label>
              <span>{application.diplomaStartedYear || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>DIPLOMA COMPLETED YEAR:</label>
              <span>{application.diplomaCompletedYear || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>BACHELOR DEGREE INSTITUTE NAME:</label>
              <span>{application.nameOfInstituteCompletedDegree || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>BACHELOR DEGREE STARTED YEAR:</label>
              <span>{application.degreeStartedYear || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>BACHELOR DEGREE COMPLETED YEAR:</label>
              <span>{application.degreeCompletedYear || 'N/A'}</span>
            </div>
            {/* <div className="info-item">
              <label>MASTER DEGREE INSTITUTE NAME:</label>
              <span>{application.masterDegreeInstituteName || 'N/A'}</span>
            </div>
            <div className="info-item"></div> */}
          </div>
        </div>

        {/* Kin/Relative Information */}
        <div className="detail-section">
          <h3>Kin/Relative Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>FIRST NAME:</label>
              <span>{application.kinfName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>MIDDLE NAME:</label>
              <span>{application.kinMname || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>LAST NAME:</label>
              <span>{application.kinlName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>RELATIONSHIP:</label>
              <span>{application.kinRelation || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Phone:</label>
              <span>{application.kinPhoneNo || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{application.kinEmail || 'N/A'}</span>
            </div>
            {/* <div className="info-item">
              <label>Address:</label>
              <span>{application.kinAddress || 'N/A'}</span>
            </div> */}
          </div>
        </div>

        {/* Financial Supporter Information */}
        <div className="detail-section">
          <h3>Financial Supporter Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Name:</label>
              <span>{application.sponsorfullName || 'N/A'}</span>
            </div>
            {/* <div className="info-item">
              <label>Relationship:</label>
              <span>{application.sponsorRelationship || 'N/A'}</span>
            </div> */}
            <div className="info-item">
              <label>Phone:</label>
              <span>{application.sponsorPhoneNo || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{application.sponsorEmail || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>address:</label>
              <span>{application.sponsorAddress || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>scholarship Request:</label>
              <span>{application.scholarshipRequest || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="detail-section">
          <h3>Additional Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Application ID:</label>
              <span>{application.id}</span>
            </div>
            <div className="info-item">
              <label>ACCOUNT ID:</label>
              <span>{application.applicantID || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>ACCOUNT EMAIL:</label>
              <span>{application.accountEmail || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>SUBMITTED AT:</label>
              <span>{formatDate(application.submittedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail; 